import { useEffect, useMemo, useRef, useState } from "react";
import { MonteurFreeTermine } from "@/components/MonteurFreeTermine";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import {
  Camera, CheckCircle2, ChevronDown, ChevronUp, Clock, Flame, HardHat, Image as ImageIcon,
  LogOut, MapPin, PhoneCall, Sun, Upload, Loader2, Sparkles, CalendarDays, FileSignature, FolderOpen, Trash2, Paperclip, StickyNote,
} from "lucide-react";
import { MonteurKomponenten, MonteurProjektbilder } from "../components/MonteurProjectInfo";
import { ProtokollDialog, PROTOKOLL_TEMPLATES } from "@/components/ProtokollDialog";

const PHASE_STATUS = {
  offen:      { label: "Offen",       cls: "bg-slate-100 text-slate-700 dark:bg-slate-800/60 dark:text-slate-300", next: "in_arbeit" },
  geplant:    { label: "Geplant",     cls: "bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300",         next: "in_arbeit" },
  in_arbeit:  { label: "In Arbeit",   cls: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300", next: "fertig" },
  fertig:     { label: "Fertig",      cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300", next: null },
  abgenommen: { label: "Abgenommen",  cls: "bg-primary/15 text-primary", next: null },
};

function fmtLocal(d) {
  const y = d.getFullYear(); const m = String(d.getMonth()+1).padStart(2,"0"); const day = String(d.getDate()).padStart(2,"0");
  return `${y}-${m}-${day}`;
}
function dayDiff(dateStr) {
  const ms = new Date(dateStr).setHours(0,0,0,0) - new Date().setHours(0,0,0,0);
  return Math.round(ms / 86_400_000);
}
function relDate(dateStr) {
  const d = dayDiff(dateStr);
  if (d === 0) return "Heute";
  if (d === 1) return "Morgen";
  if (d === -1) return "Gestern";
  if (d > 1 && d < 7) return `in ${d} Tagen`;
  if (d < -1 && d > -7) return `vor ${-d} Tagen`;
  return dateStr;
}

export default function MonteurPortal() {
  const { user, logout } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openPhase, setOpenPhase] = useState(null); // `${projectId}:${phaseKey}`
  const [uploading, setUploading] = useState(null); // phaseKey while uploading
  const [protokoll, setProtokoll] = useState(null); // { projectId, phaseKey, kundeName }

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/projects");
      setProjects(data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Projekte konnten nicht geladen werden");
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  // Nur Phasen, die diesem Monteur zugewiesen sind, sammeln + nach Datum sortieren
  const myAssignments = useMemo(() => {
    const out = [];
    for (const p of projects) {
      for (const ph of (p.phases || [])) {
        if (ph.monteur_id === user?.id) {
          out.push({ project: p, phase: ph });
        }
      }
    }
    out.sort((a, b) => {
      const ta = (a.phase.termin || "9999-12-31") + (a.phase.uhrzeit_von || "99");
      const tb = (b.phase.termin || "9999-12-31") + (b.phase.uhrzeit_von || "99");
      return ta.localeCompare(tb);
    });
    return out;
  }, [projects, user?.id]);

  const today = fmtLocal(new Date());
  const endOf = (ph) => (ph.termin_ende && ph.termin_ende > ph.termin ? ph.termin_ende : ph.termin);
  const todayItems = myAssignments.filter((x) => x.phase.termin && x.phase.termin <= today && endOf(x.phase) >= today);
  const upcoming = myAssignments.filter((x) => !x.phase.termin || x.phase.termin > today);
  const past = myAssignments.filter((x) => x.phase.termin && endOf(x.phase) < today);

  const openPhaseKey = (pid, pk) => `${pid}:${pk}`;
  const togglePhase = (pid, pk) => setOpenPhase((cur) => cur === openPhaseKey(pid, pk) ? null : openPhaseKey(pid, pk));

  const uploadPhoto = async (projectId, phaseKey, file) => {
    if (!file) return;
    setUploading(openPhaseKey(projectId, phaseKey));
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/projects/${projectId}/phases/${phaseKey}/photos`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("📸 Foto hochgeladen");
      await refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload fehlgeschlagen");
    } finally { setUploading(null); }
  };

  const uploadMultiple = async (projectId, phaseKey, files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setUploading(openPhaseKey(projectId, phaseKey));
    let ok = 0;
    for (const f of list) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        await api.post(`/projects/${projectId}/phases/${phaseKey}/photos`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        ok++;
      } catch {}
    }
    if (ok === list.length) toast.success(`${ok}/${list.length} Foto${list.length === 1 ? "" : "s"} hochgeladen`);
    else toast.error(`Nur ${ok}/${list.length} Foto${list.length === 1 ? "" : "s"} hochgeladen – bitte erneut versuchen`);
    await refresh();
    setUploading(null);
  };

  const setStatus = async (project, phaseKey, newStatus) => {
    const newPhases = (project.phases || []).map((p) => p.key === phaseKey ? { ...p, status: newStatus } : p);
    try {
      await api.patch(`/projects/${project.id}`, { phases: newPhases });
      toast.success(`Status → ${PHASE_STATUS[newStatus]?.label || newStatus}`);
      await refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Status-Update fehlgeschlagen");
    }
  };

  const saveNotes = async (project, phaseKey, notizen) => {
    const newPhases = (project.phases || []).map((p) => p.key === phaseKey ? { ...p, notizen } : p);
    try {
      await api.patch(`/projects/${project.id}`, { phases: newPhases });
      await refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Notiz-Speichern fehlgeschlagen");
    }
  };

  const handleLogout = async () => { await logout(); window.location.href = "/login"; };

  if (loading && projects.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-24" data-testid="monteur-portal">
      {/* Mobile-first Header */}
      <header className="sticky top-0 z-30 bg-card/95 backdrop-blur border-b border-border px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <div className="h-9 w-9 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0">
            <HardHat className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Monteur-Portal</div>
            <div className="text-sm font-semibold truncate" data-testid="monteur-name">{user?.name || user?.email}</div>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="h-10 w-10 rounded-full hover:bg-secondary flex items-center justify-center text-muted-foreground"
          data-testid="monteur-logout"
          aria-label="Abmelden"
        >
          <LogOut className="h-5 w-5" />
        </button>
      </header>

      <div className="px-4 py-6 max-w-xl mx-auto space-y-6">
        {/* Heute-Hero */}
        <section data-testid="section-today">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="h-4 w-4 text-primary" />
            <h2 className="font-heading text-lg font-bold tracking-tight">Heute</h2>
            <span className="ml-auto text-[11px] text-muted-foreground">{new Date().toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "short" })}</span>
          </div>
          {todayItems.length === 0 ? (
            <Card className="border-dashed border-border">
              <CardContent className="p-6 text-center text-sm text-muted-foreground">
                Keine Projekt-Einsätze für heute. Sonstige Termine siehe unten.
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {todayItems.map(({ project, phase }) => (
                <AssignmentCard
                  key={`t-${project.id}-${phase.key}`}
                  project={project}
                  phase={phase}
                  highlighted
                  isOpen={openPhase === openPhaseKey(project.id, phase.key)}
                  onToggle={() => togglePhase(project.id, phase.key)}
                  uploading={uploading === openPhaseKey(project.id, phase.key)}
                  onUpload={(file) => uploadPhoto(project.id, phase.key, file)}
                  onUploadMany={(files) => uploadMultiple(project.id, phase.key, files)}
                  onStatus={(s) => setStatus(project, phase.key, s)}
                  onNotes={(n) => saveNotes(project, phase.key, n)}
                  onProtokoll={() => setProtokoll({ projectId: project.id, phaseKey: phase.key, kundeName: project.kunde_name })}
                />
              ))}
            </div>
          )}
        </section>

        <MonteurFreeTermine today={today} />

        {/* Anstehende Einsätze */}
        {upcoming.length > 0 && (
          <section data-testid="section-upcoming">
            <div className="flex items-center gap-2 mb-3">
              <CalendarDays className="h-4 w-4 text-sky-500" />
              <h2 className="font-heading text-lg font-bold tracking-tight">Anstehend</h2>
              <span className="ml-auto text-[11px] text-muted-foreground">{upcoming.length}</span>
            </div>
            <div className="space-y-3">
              {upcoming.map(({ project, phase }) => (
                <AssignmentCard
                  key={`u-${project.id}-${phase.key}`}
                  project={project}
                  phase={phase}
                  isOpen={openPhase === openPhaseKey(project.id, phase.key)}
                  onToggle={() => togglePhase(project.id, phase.key)}
                  uploading={uploading === openPhaseKey(project.id, phase.key)}
                  onUpload={(file) => uploadPhoto(project.id, phase.key, file)}
                  onUploadMany={(files) => uploadMultiple(project.id, phase.key, files)}
                  onStatus={(s) => setStatus(project, phase.key, s)}
                  onNotes={(n) => saveNotes(project, phase.key, n)}
                  onProtokoll={() => setProtokoll({ projectId: project.id, phaseKey: phase.key, kundeName: project.kunde_name })}
                />
              ))}
            </div>
          </section>
        )}

        {/* Erledigte / Alte */}
        {past.length > 0 && (
          <section data-testid="section-past">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle2 className="h-4 w-4 text-emerald-500" />
              <h2 className="font-heading text-lg font-bold tracking-tight">Zurückliegend</h2>
              <span className="ml-auto text-[11px] text-muted-foreground">{past.length}</span>
            </div>
            <div className="space-y-3">
              {past.map(({ project, phase }) => (
                <AssignmentCard
                  key={`p-${project.id}-${phase.key}`}
                  project={project}
                  phase={phase}
                  dimmed
                  isOpen={openPhase === openPhaseKey(project.id, phase.key)}
                  onToggle={() => togglePhase(project.id, phase.key)}
                  uploading={uploading === openPhaseKey(project.id, phase.key)}
                  onUpload={(file) => uploadPhoto(project.id, phase.key, file)}
                  onUploadMany={(files) => uploadMultiple(project.id, phase.key, files)}
                  onStatus={(s) => setStatus(project, phase.key, s)}
                  onNotes={(n) => saveNotes(project, phase.key, n)}
                  onProtokoll={() => setProtokoll({ projectId: project.id, phaseKey: phase.key, kundeName: project.kunde_name })}
                />
              ))}
            </div>
          </section>
        )}

        {myAssignments.length === 0 && (
          <Card className="border-dashed border-border">
            <CardContent className="p-10 text-center">
              <HardHat className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
              <div className="text-sm text-muted-foreground">
                Dir sind aktuell keine Phasen zugewiesen.
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Protokoll-Dialog (projekt-weit, da nur eins gleichzeitig offen) */}
      {protokoll && (
        <ProtokollDialog
          open={!!protokoll}
          onOpenChange={(v) => { if (!v) setProtokoll(null); }}
          projectId={protokoll.projectId}
          phaseKey={protokoll.phaseKey}
          kundeName={protokoll.kundeName}
          onDone={() => { setProtokoll(null); refresh(); }}
        />
      )}
    </div>
  );
}

function AssignmentCard({
  project, phase, highlighted, dimmed, isOpen, onToggle,
  uploading, onUpload, onUploadMany, onStatus, onNotes, onProtokoll,
}) {
  const statusMeta = PHASE_STATUS[phase.status] || PHASE_STATUS.offen;
  const { user } = useAuth();
  const [notesSeen, setNotesSeen] = useState(false);
  const notesUnseen = !notesSeen && Boolean(project.notizen_updated_at)
    && !(project.notizen_seen_by || []).includes(user?.id);
  useEffect(() => {
    if (!isOpen || !notesUnseen) return;
    const t = setTimeout(() => {
      api.post(`/projects-notes/${project.id}/seen`).then(() => setNotesSeen(true)).catch(() => {});
    }, 1500);
    return () => clearTimeout(t);
  }, [isOpen, notesUnseen, project.id]);
  useEffect(() => { setNotesSeen(false); }, [project.notizen_updated_at]);
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);
  const projectFileRef = useRef(null);
  const [notes, setNotes] = useState(phase.notizen || "");
  const notesTimer = useRef(null);
  const notesDirty = useRef(false);
  const [projectFiles, setProjectFiles] = useState([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [uploadingProjectFile, setUploadingProjectFile] = useState(false);
  const hasProtokoll = !!PROTOKOLL_TEMPLATES[phase.key];

  // Sync nur, wenn nicht gerade editiert
  useEffect(() => {
    if (!notesDirty.current) setNotes(phase.notizen || "");
  }, [phase.notizen]);

  const onNotesChange = (v) => {
    notesDirty.current = true;
    setNotes(v);
    if (notesTimer.current) clearTimeout(notesTimer.current);
    notesTimer.current = setTimeout(() => {
      notesDirty.current = false;
      onNotes(v);
    }, 1200);
  };
  const flushNotes = () => {
    if (notesTimer.current) { clearTimeout(notesTimer.current); notesTimer.current = null; }
    if (notesDirty.current && notes !== (phase.notizen || "")) {
      notesDirty.current = false;
      onNotes(notes);
    } else { notesDirty.current = false; }
  };

  const loadProjectFiles = async () => {
    setFilesLoading(true);
    try {
      const { data } = await api.get(`/projects/${project.id}/documents`);
      setProjectFiles(data || []);
    } catch { setProjectFiles([]); }
    finally { setFilesLoading(false); }
  };

  useEffect(() => {
    if (isOpen) loadProjectFiles();
    // eslint-disable-next-line
  }, [isOpen]);

  const uploadProjectFile = async (file) => {
    if (!file) return;
    setUploadingProjectFile(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/projects/${project.id}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("📎 Datei hochgeladen");
      await loadProjectFiles();
    } catch {
      toast.error("Upload fehlgeschlagen");
    } finally { setUploadingProjectFile(false); }
  };

  const deleteProjectFile = async (docId) => {
    try {
      await api.delete(`/projects/${project.id}/documents/${docId}`);
      toast.success("Datei gelöscht");
      await loadProjectFiles();
    } catch {
      toast.error("Löschen fehlgeschlagen");
    }
  };

  const plzOrt = [project.kunde_plz, project.kunde_ort].filter(Boolean).join(" ");
  const fullAddress = [project.kunde_adresse, plzOrt && !(project.kunde_adresse || "").includes(plzOrt) ? plzOrt : ""]
    .filter(Boolean).join(", ");
  const mapsUrl = fullAddress
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`
    : null;

  const photos = phase.photos || [];
  const tel = project.telefon || project.kunde_telefon || "";
  const telHref = `tel:${tel.replace(/[^\d+]/g, "")}`;

  return (
    <Card
      className={`transition-all border ${
        highlighted ? "border-primary shadow-md ring-2 ring-primary/30" :
        dimmed ? "border-border/60 opacity-80" :
        "border-border"
      }`}
      data-testid={`assignment-${project.id}-${phase.key}`}
    >
      <CardContent className="p-0">
        {/* Collapsed header */}
        <button
          onClick={onToggle}
          className="w-full p-4 text-left flex items-start gap-3"
          data-testid={`assignment-toggle-${project.id}-${phase.key}`}
        >
          <div className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 ${project.project_type === "pv" ? "bg-primary/10 text-primary" : "bg-orange-100 dark:bg-orange-900/20 text-orange-500"}`}>
            {project.project_type === "pv" ? <Sun className="h-5 w-5" /> : <Flame className="h-5 w-5" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <div className="font-semibold text-sm truncate">{project.kunde_name}</div>
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${statusMeta.cls} shrink-0`} data-testid={`assignment-status-${phase.key}`}>
                {statusMeta.label}
              </span>
            </div>
            {fullAddress && (
              <div className="text-xs text-foreground/80 mt-0.5 break-words" data-testid={`assignment-address-${phase.key}`}>{fullAddress}</div>
            )}
            {tel && (
              <span
                role="link"
                tabIndex={0}
                onClick={(e) => { e.stopPropagation(); window.location.href = telHref; }}
                onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); window.location.href = telHref; } }}
                className="inline-flex items-center gap-1.5 text-xs mt-1 px-2 py-1 rounded-md bg-primary/10 text-primary font-medium hover:bg-primary hover:text-primary-foreground transition-colors"
                data-testid={`assignment-phone-${phase.key}`}
              >
                <PhoneCall className="h-3.5 w-3.5" /> {tel}
              </span>
            )}
            <div className="text-xs text-muted-foreground truncate mt-0.5">{phase.label}</div>
            {notesUnseen && (
              <div className="inline-flex items-center gap-1 text-[11px] mt-1.5 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 font-medium" data-testid={`assignment-notes-changed-${phase.key}`}>
                <StickyNote className="h-3 w-3" /> Notizen geändert
              </div>
            )}
            {phase.termin && (
              <div className="flex items-center gap-1.5 text-[11px] mt-1.5 text-muted-foreground">
                <Clock className="h-3 w-3" />
                <span className="font-medium">
                  {phase.termin_ende && phase.termin_ende > phase.termin && dayDiff(phase.termin) <= 0 && dayDiff(phase.termin_ende) >= 0
                    ? `Heute · Tag ${1 - dayDiff(phase.termin)}/${dayDiff(phase.termin_ende) - dayDiff(phase.termin) + 1}`
                    : relDate(phase.termin)}
                </span>
                <span className="font-mono opacity-60" data-testid={`assignment-termin-${phase.key}`}>· {phase.termin}{phase.termin_ende && phase.termin_ende > phase.termin ? ` – ${phase.termin_ende}` : ""}{phase.uhrzeit_von ? ` · ${phase.uhrzeit_von}${phase.uhrzeit_bis ? `–${phase.uhrzeit_bis}` : ""}` : ""}</span>
              </div>
            )}
            {photos.length > 0 && (
              <div className="flex items-center gap-1 text-[11px] mt-1 text-muted-foreground">
                <ImageIcon className="h-3 w-3" /> {photos.length} Foto{photos.length === 1 ? "" : "s"}
              </div>
            )}
          </div>
          {isOpen ? <ChevronUp className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" /> : <ChevronDown className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />}
        </button>

        {/* Expanded body */}
        {isOpen && (
          <div className="border-t border-border/60 bg-muted/20 p-4 space-y-4" data-testid={`assignment-body-${phase.key}`}>
            {/* Adresse & Telefon-Quick-Actions */}
            <div className="grid grid-cols-2 gap-2">
              <a
                href={mapsUrl || "#"}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => !mapsUrl && e.preventDefault()}
                className={`flex items-center justify-center gap-2 h-11 rounded-md border border-border text-sm font-medium ${mapsUrl ? "hover:bg-primary hover:text-primary-foreground hover:border-primary" : "opacity-50 cursor-not-allowed"}`}
                data-testid={`assignment-route-${phase.key}`}
              >
                <MapPin className="h-4 w-4" /> Route
              </a>
              <a
                href={tel ? telHref : "#"}
                onClick={(e) => !tel && e.preventDefault()}
                className={`flex items-center justify-center gap-2 h-11 rounded-md border border-border text-sm font-medium ${tel ? "hover:bg-primary hover:text-primary-foreground hover:border-primary" : "opacity-50 cursor-not-allowed"}`}
                data-testid={`assignment-call-${phase.key}`}
              >
                <PhoneCall className="h-4 w-4" /> {tel ? "Anrufen" : "Keine Nummer"}
              </a>
            </div>
            {fullAddress && (
              <div className="text-xs text-muted-foreground px-1" data-testid={`assignment-full-address-${phase.key}`}>📍 {fullAddress}</div>
            )}

            {/* Direkt-Foto-Buttons */}
            <div className="grid grid-cols-2 gap-2">
              <input
                ref={cameraRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                data-testid={`assignment-camera-input-${phase.key}`}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = "";
                  if (f) onUpload(f);
                }}
              />
              <input
                ref={galleryRef}
                type="file"
                accept="image/*,application/pdf"
                multiple
                className="hidden"
                data-testid={`assignment-gallery-input-${phase.key}`}
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  e.target.value = "";
                  if (files.length) onUploadMany(files);
                }}
              />
              <Button
                type="button"
                size="lg"
                onClick={() => cameraRef.current?.click()}
                disabled={!!uploading}
                className="h-14 text-base"
                data-testid={`assignment-camera-${phase.key}`}
              >
                {uploading ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : <Camera className="h-5 w-5 mr-2" />}
                Foto aufnehmen
              </Button>
              <Button
                type="button"
                variant="outline"
                size="lg"
                onClick={() => galleryRef.current?.click()}
                disabled={!!uploading}
                className="h-14 text-base"
                data-testid={`assignment-gallery-${phase.key}`}
              >
                <Upload className="h-5 w-5 mr-2" /> Hochladen
              </Button>
            </div>

            {/* Foto-Thumbnails */}
            {photos.length > 0 && (
              <div className="space-y-2">
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Fotos ({photos.length})</div>
                <div className="grid grid-cols-3 gap-2">
                  {photos.map((p) => (
                    <a
                      key={p.id}
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/documents/${p.path}`}
                      target="_blank"
                      rel="noreferrer"
                      className="relative aspect-square rounded-md overflow-hidden bg-muted border border-border group"
                      data-testid={`assignment-photo-${p.id}`}
                    >
                      {p.path?.endsWith?.(".pdf") ? (
                        <div className="w-full h-full flex items-center justify-center text-muted-foreground text-[10px]">
                          <ImageIcon className="h-5 w-5" />
                          <span className="ml-1">PDF</span>
                        </div>
                      ) : (
                        <img
                          src={`${process.env.REACT_APP_BACKEND_URL}/api/documents/${p.path}`}
                          alt={p.name}
                          loading="lazy"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                        />
                      )}
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Status-Quick-Buttons */}
            <div className="space-y-2">
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Status</div>
              <div className="grid grid-cols-2 gap-2">
                {["in_arbeit", "fertig"].map((s) => (
                  <Button
                    key={s}
                    type="button"
                    variant={phase.status === s ? "default" : "outline"}
                    size="lg"
                    onClick={() => onStatus(s)}
                    className="h-12 text-sm"
                    data-testid={`assignment-set-status-${phase.key}-${s}`}
                  >
                    {s === "in_arbeit" ? <Clock className="h-4 w-4 mr-1.5" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />}
                    {PHASE_STATUS[s].label}
                  </Button>
                ))}
              </div>
            </div>

            {/* Abnahmeprotokoll — nur wenn Template existiert für diese Phase */}
            {hasProtokoll && (
              <div className="space-y-1.5">
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Abnahme</div>
                <Button
                  type="button"
                  variant={phase.status === "abgenommen" ? "outline" : "default"}
                  size="lg"
                  onClick={onProtokoll}
                  className="w-full h-14 text-base"
                  data-testid={`assignment-protokoll-${phase.key}`}
                >
                  <FileSignature className="h-5 w-5 mr-2" />
                  {phase.status === "abgenommen" ? "Protokoll neu erstellen" : "Abnahmeprotokoll erstellen"}
                </Button>
              </div>
            )}

            {/* Projekt-Dateiablage */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                  <FolderOpen className="h-3.5 w-3.5" /> Dateiablage
                </div>
                {filesLoading && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
              </div>
              <input
                ref={projectFileRef}
                type="file"
                accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx"
                className="hidden"
                data-testid={`assignment-project-file-input-${phase.key}`}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = "";
                  if (f) uploadProjectFile(f);
                }}
              />
              <Button
                type="button"
                variant="outline"
                size="lg"
                onClick={() => projectFileRef.current?.click()}
                disabled={uploadingProjectFile}
                className="w-full h-12 text-sm"
                data-testid={`assignment-project-file-upload-${phase.key}`}
              >
                {uploadingProjectFile ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Paperclip className="h-4 w-4 mr-2" />}
                Datei zum Projekt hochladen
              </Button>
              {projectFiles.length > 0 && (
                <ul className="divide-y divide-border/60 rounded-md border border-border/60 bg-background" data-testid={`assignment-project-files-${phase.key}`}>
                  {projectFiles.map((f) => (
                    <li key={f.id} className="flex items-center gap-2 px-2 py-2">
                      {f.content_type?.startsWith("image/") ? (
                        <ImageIcon className="h-4 w-4 text-muted-foreground shrink-0" />
                      ) : (
                        <Paperclip className="h-4 w-4 text-muted-foreground shrink-0" />
                      )}
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/documents/${f.path}`}
                        target="_blank"
                        rel="noreferrer"
                        className="flex-1 min-w-0 text-xs truncate text-foreground hover:text-primary hover:underline"
                        data-testid={`project-file-${f.id}`}
                      >
                        {f.name || f.path?.split("/").pop()}
                      </a>
                      <button
                        type="button"
                        onClick={() => deleteProjectFile(f.id)}
                        className="h-7 w-7 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive flex items-center justify-center shrink-0"
                        aria-label="Löschen"
                        data-testid={`project-file-delete-${f.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <MonteurKomponenten project={project} phaseKey={phase.key} />
            <MonteurProjektbilder project={project} phaseKey={phase.key} />

            {/* Projektnotizen (nur lesen) */}
            <div className="space-y-1.5" data-testid={`assignment-project-notizen-${phase.key}`}>
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                <StickyNote className="h-3.5 w-3.5" /> Projektnotizen
              </div>
              <div className={`rounded-md border px-3 py-2 text-sm whitespace-pre-line ${notesUnseen ? "border-amber-400 bg-amber-50 dark:bg-amber-900/20" : "border-border/60 bg-muted/40"}`}>
                {project.notizen?.trim() ? project.notizen : <span className="text-muted-foreground italic text-xs">Keine Projektnotizen</span>}
              </div>
            </div>

            {/* Notizen */}
            <div className="space-y-1.5">
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Notizen</div>
              <Textarea
                rows={3}
                value={notes}
                onChange={(e) => onNotesChange(e.target.value)}
                onBlur={flushNotes}
                placeholder="Was ist auf der Baustelle los? …"
                className="text-sm"
                data-testid={`assignment-notes-${phase.key}`}
              />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
